
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ResetPasswordDialog = ({ open, onOpenChange }: ResetPasswordDialogProps) => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const handleDeepLink = async ({ url }: { url: string }) => {
        console.log('Deep link URL received:', url);
        if (url.includes('#access_token=')) {
          const accessToken = url.split('#access_token=')[1].split('&')[0];
          const refreshTokenValue = url.split('refresh_token=')[1]?.split('&')[0] || '';
          
          console.log('Extracted tokens:', { accessToken, refreshToken: refreshTokenValue });

          try {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshTokenValue,
            });
            
            if (error) {
              console.error('Error setting session:', error);
              toast({
                title: "Error",
                description: "Failed to validate reset token. Please try again.",
                variant: "destructive",
              });
              return;
            }
            
            toast({
              title: "Success",
              description: "Password has been reset successfully.",
            });

          } catch (error) {
            console.error('Session error:', error);
          }
        }
      };

      App.addListener('appUrlOpen', handleDeepLink);

      return () => {
        App.removeAllListeners();
      };
    }
  }, [toast]);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResetting(true);
    setResetError('');

    try {
      // Use native app scheme if on native platform
      const redirectTo = Capacitor.isNativePlatform() 
        ? 'https://ios.mindnote.online/reset-password'
        : 'https://workspace.mindnote.online/auth/callback';
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });

      if (error) {
        console.error('Auth error:', error);
        setResetError('Unable to send password reset email. Please try again.');
        return;
      }

      toast({
        title: "Password reset link sent",
        description: "Please check your email and click the link to reset your password.",
      });

      onOpenChange(false);
    } catch (error) {
      console.error('Unexpected error during password reset process:', error);
      setResetError('An unexpected error occurred. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Password Recovery</DialogTitle>
          <DialogDescription>
            Enter your email address and we'll send you a magic link to sign in and access your account.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleMagicLink} className="space-y-4">
          {resetError && (
            <Alert variant="destructive">
              <AlertDescription>{resetError}</AlertDescription>
            </Alert>
          )}
          <div className="grid gap-2">
            <label htmlFor="email" className="text-sm font-medium text-gray-700">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              disabled={isResetting}
              className="w-full"
            />
          </div>
          <Button type="submit" className="w-full" disabled={isResetting}>
            {isResetting ? "Sending..." : "Send Recovery Link"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
